/**
 * Collector-related constants
 * Centralized location for all hardcoded values used throughout collector pages
 *
 * @overview
 * This module provides constants for the unified collector system.
 * The dashboard adapts based on user capabilities (wallet, artworks, orders)
 * rather than a pre-assigned collector type.
 */

/**
 * Route Paths
 *
 * @description
 * Centralized route definitions for all collector pages.
 * All routes are plain strings under /collector/*.
 *
 * @example
 * COLLECTOR_ROUTES.DASHBOARD        // => /collector
 * COLLECTOR_ROUTES.ARTWORKS         // => /collector/artworks
 * COLLECTOR_ROUTES.PUBLIC_PROFILE('john')  // => /collector/john
 */
export const COLLECTOR_ROUTES = {
  DASHBOARD: '/collector',
  ARTWORKS: '/collector/artworks',
  FINANCIAL: '/collector/financial',
  REWARDS: '/collector/rewards',
  ACTIVITY: '/collector/activity',
  ORDERS: '/collector/orders',
  ACCOUNT: '/collector/account',
  CLAIM: '/collector/claim',
  COMMISSION: '/collector/commission',
  COMMISSION_DIGITAL: '/collector/commission/digital',
  COMMISSION_PHYGITAL: '/collector/commission/phygital',
  ONBOARDING: '/collector/onboarding',
  PUBLIC_PROFILE: (username: string) => `/collector/${username}`,
} as const;

/**
 * Onboarding Steps
 */
export const ONBOARDING_STEPS = {
  USERNAME: 1,
  ARTWORKS: 2,
  WALLET: 3,
  PROFILE: 4,
} as const;

/**
 * Wizard Configuration
 */
export const WIZARD_CONFIG = {
  ANIMATION_DURATION: 0.4,
  STEP_TRANSITION_EASE: [0.4, 0, 0.2, 1] as const,
} as const;

/**
 * Commission Wizard Steps
 */
export const COMMISSION_STEPS = {
  PHYGITAL: {
    SELECT_ARTWORK: { id: 1, name: 'SELECT ARTWORK' },
    CHOOSE_OBJECT: { id: 2, name: 'CHOOSE OBJECT' },
    CHECKOUT: { id: 3, name: 'CHECKOUT' },
  },
  DIGITAL: {
    SELECT_TYPE: { id: 1, name: 'SELECT TYPE' },
    DESCRIBE_VISION: { id: 2, name: 'DESCRIBE VISION' },
    REVIEW: { id: 3, name: 'REVIEW & SUBMIT' },
  },
} as const;

/**
 * Dashboard Card Configuration
 * Cards with requiresArtworks: true are only shown when user has artworks.
 * Cards with featureFlag are only shown when that feature is enabled.
 * The 'profile' card uses a dynamic href resolved at render time.
 */
export type DashboardCardIcon =
  | 'Archive'
  | 'Download'
  | 'Paintbrush'
  | 'TrendingUp'
  | 'ShoppingCart'
  | 'User'
  | 'Activity'
  | 'Gift';

export interface DashboardCard {
  id: string;
  title: string;
  subtitle: string;
  icon: DashboardCardIcon;
  href: string | null;
  requiresArtworks: boolean;
  featureFlag?: 'collectorRewards' | 'collectorCommissions';
}

export const DASHBOARD_CARDS: DashboardCard[] = [
  { id: 'claim', title: 'CLAIM PRIZE', subtitle: 'CONVERSATIONS', icon: 'Gift', href: COLLECTOR_ROUTES.CLAIM, requiresArtworks: false },
  { id: 'art-vault', title: 'VIEW ART VAULT', subtitle: 'COLLECTION', icon: 'Archive', href: COLLECTOR_ROUTES.ARTWORKS, requiresArtworks: true },
  { id: 'downloads', title: 'DOWNLOAD HI-RES', subtitle: 'ARTWORK FILES', icon: 'Download', href: COLLECTOR_ROUTES.ARTWORKS, requiresArtworks: true },
  { id: 'orders', title: 'CHECK ORDERS', subtitle: 'ORDER HISTORY', icon: 'ShoppingCart', href: COLLECTOR_ROUTES.ORDERS, requiresArtworks: false },
  { id: 'account', title: 'ADDRESSES', subtitle: 'ACCOUNT', icon: 'User', href: COLLECTOR_ROUTES.ACCOUNT, requiresArtworks: false },
  // { id: 'commission', title: 'ORDER ARTEFACTS', subtitle: 'COMMISSION', icon: 'Paintbrush', href: COLLECTOR_ROUTES.COMMISSION, requiresArtworks: false, featureFlag: 'collectorCommissions' },
  { id: 'financial', title: 'SEE ANALYTICS', subtitle: 'PORTFOLIO', icon: 'TrendingUp', href: COLLECTOR_ROUTES.FINANCIAL, requiresArtworks: true },
  { id: 'activity', title: 'VIEW ACTIVITY', subtitle: 'ON-CHAIN', icon: 'Activity', href: COLLECTOR_ROUTES.ACTIVITY, requiresArtworks: true },
  // { id: 'profile', title: 'PUBLIC PROFILE', subtitle: 'MY PAGE', icon: 'User', href: null, requiresArtworks: false },
  { id: 'rewards', title: 'CLAIM REWARDS', subtitle: 'AIRDROPS', icon: 'Gift', href: COLLECTOR_ROUTES.REWARDS, requiresArtworks: true, featureFlag: 'collectorRewards' },
];

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
  BID: 'bid',
  SALE: 'sale',
  PLATFORM: 'platform',
  EVENT: 'event',
  ORDER_SHIPPED: 'order_shipped',
  ORDER_PROCESSING: 'order_processing',
  ORDER_DELIVERED: 'order_delivered',
  ORDER_CONFIRMED: 'order_confirmed',
  PRODUCT_RESTOCKED: 'product_restocked',
} as const;

/**
 * UI Configuration
 */
export const COLLECTOR_UI_CONFIG = {
  ANIMATION_DURATION: 400,
  FADE_IN_DURATION: 0.6,
  FADE_IN_EASE: [0.22, 1, 0.36, 1] as const,
  STAGGER_DELAY: 0.15,
  STAGGER_CHILDREN_DELAY: 0.2,
} as const;

/**
 * Grid Configurations
 */
export const GRID_CONFIG = {
  ARTWORK_SELECTION: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  OBJECT_SELECTION: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  COMMISSION_SUMMARY: 'grid-cols-1 sm:grid-cols-2',
} as const;

/**
 * Error Messages
 */
export const COLLECTOR_ERROR_MESSAGES = {
  INVALID_TYPE: 'Invalid collector type',
  NOT_FOUND: 'Collector page not found',
  LOAD_FAILED: 'Failed to load collector data',
  SAVE_FAILED: 'Failed to save changes',
  UPLOAD_FAILED: 'Failed to upload file',
} as const;

/**
 * Success Messages
 */
export const COLLECTOR_SUCCESS_MESSAGES = {
  PROFILE_UPDATED: 'Profile updated successfully',
  SETTINGS_SAVED: 'Settings saved',
  COMMISSION_SUBMITTED: 'Commission request submitted',
  ONBOARDING_COMPLETE: 'Welcome to your collector dashboard!',
} as const;

/**
 * Physical Product Options
 */
export const PHYSICAL_PRODUCT_OPTIONS = [
  {
    id: 'fine-art-print',
    title: 'FINE ART PRINT',
    description: 'Museum-quality archival print on premium paper',
    price: 'Starting at $250',
    sizes: ['12" × 16"', '16" × 20"', '20" × 24"', '24" × 30"'],
  },
  {
    id: 'canvas',
    title: 'CANVAS',
    description: 'Gallery-wrapped canvas with museum-quality printing',
    price: 'Starting at $450',
    sizes: ['16" × 20"', '20" × 24"', '24" × 30"', '30" × 40"'],
  },
  {
    id: 'digital-display',
    title: 'DIGITAL DISPLAY',
    description: 'High-resolution digital frame with your artwork pre-loaded',
    price: 'Starting at $1,200',
    sizes: ['21.5"', '27"', '32"', '43"'],
  },
  {
    id: 'handmade-rug',
    title: 'HAND-MADE RUG',
    description: 'Custom hand-woven rug featuring your digital artwork',
    price: 'Starting at $3,500',
    sizes: ['3\' × 5\'', '4\' × 6\'', '5\' × 7\'', '6\' × 9\''],
  },
] as const;

/**
 * Sample Collections (for demo purposes)
 */
export const SAMPLE_COLLECTIONS = [
  { id: 1, name: 'GENESIS COLLECTION', artworkCount: 12 },
  { id: 2, name: 'ABSTRACT SERIES', artworkCount: 8 },
  { id: 3, name: 'DIGITAL NATURE', artworkCount: 15 },
  { id: 4, name: 'URBAN LANDSCAPES', artworkCount: 6 },
] as const;

/**
 * Sample Artworks by Collection (for demo purposes)
 */
export const SAMPLE_ARTWORKS_BY_COLLECTION: Record<number, Array<{ id: number; title: string }>> = {
  1: [
    { id: 101, title: 'GENESIS #1' },
    { id: 102, title: 'GENESIS #2' },
    { id: 103, title: 'GENESIS #3' },
    { id: 104, title: 'GENESIS #4' },
  ],
  2: [
    { id: 201, title: 'ABSTRACT FLOW' },
    { id: 202, title: 'CHROMATIC WAVES' },
    { id: 203, title: 'VOID PATTERNS' },
  ],
  3: [
    { id: 301, title: 'DIGITAL BLOOM' },
    { id: 302, title: 'SYNTHETIC FOREST' },
    { id: 303, title: 'PIXEL MEADOW' },
    { id: 304, title: 'CODE GARDEN' },
  ],
  4: [
    { id: 401, title: 'CITY LIGHTS' },
    { id: 402, title: 'METRO LINES' },
    { id: 403, title: 'CONCRETE DREAMS' },
  ],
} as const;

/**
 * Digital Artefact Types (for commission flow)
 */
export const DIGITAL_ARTEFACT_TYPES = [
  {
    id: 'gen-ai-art',
    title: 'GEN AI ART',
    description: 'Unique artwork created through AI algorithms and artist collaboration',
    price: 'Starting at $2,500',
    deliveryTime: '2-4 weeks',
  },
  {
    id: 'generative-art',
    title: 'GENERATIVE ART',
    description: 'Code-based artwork with algorithmically generated variations',
    price: 'Starting at $3,000',
    deliveryTime: '3-5 weeks',
  },
  {
    id: 'cgi-art',
    title: 'CGI ART',
    description: 'Computer-generated imagery and 3D digital compositions',
    price: 'Starting at $3,500',
    deliveryTime: '4-6 weeks',
  },
] as const;
